import type { LiveTrackPendingPrimarySave } from '@/trackMatching/trackMatchingQueueState';

export const SOURCE_TYPES = {
  SPOTIFY: 'Spotify',
  RAW_DDEX: 'Ddex',
  LIVE_TRACK: 'Live Track',
} as const;

export type SourceType = (typeof SOURCE_TYPES)[keyof typeof SOURCE_TYPES];

/** Synthetic `source` for rows that only render Raw DDEX list expand/collapse in the Actions column */
export const RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE = '__raw_ddex_expand__' as const;

export interface TrackMatchingAction {
  live_track_id: string | number;
  live_track_code: string;
  source: 'track_matching' | 'search_view';
  update_audio_file: string | null;
  update_cover_art: string | null;
  update_fields: Record<string, unknown> | null;
  raw_ddex_track_id?: number | null;
  live_track_backup: Record<string, unknown> | null;
}

export interface TrackMatchingRowState {
  live_track_id: string | number;
  source: SourceType | string;
  sourceId: string | number;
  matchData?: Record<string, unknown>;
  /** Live track row only: queued link target or Apply ISRC (mutually exclusive). */
  pendingPrimarySave?: LiveTrackPendingPrimarySave | null;
  /**
   * Live track row only: queued artist-name cleanup sourced from a Raw DDEX / Spotify row.
   * Independent of {@link pendingPrimarySave} — an artist clean can be saved alongside a Link
   * or Apply ISRC (the ticket treats it as a standalone queue action, not part of Link).
   * `sourceId` is the clicked match row id; `name` is the cleaner artist name to write.
   */
  pendingArtistRename?: { sourceId: string | number; name: string } | null;
  /** Live track row: unlink queued. */
  unlinkQueued?: boolean;
  /** Live track row: Later status (or unmark recompute) queued. */
  markQueued?: boolean;
  /** Live track row: user expanded candidates after Linked/Later. */
  reveal_done_matches?: boolean;
}

/** UI / store queue toggles (Raw DDEX link, Spotify ISRC, live-track unlink). */
export type TrackQueueAction = 'link' | 'isrc' | 'unlink';

/** Filter options for `track_match_types` on the track-matching page. */
export const TRACK_MATCH_TYPES = {
  SPOTIFY_ISRC: 'Spotify ISRC',
  SPOTIFY_FUZZY: 'Spotify Fuzzy',
  DDEX_ISRC: 'Ddex ISRC',
  DDEX_FUZZY: 'Ddex Fuzzy',
  DDEX_FINGERPRINT: 'Ddex Fingerprint',
} as const;
